import React, { useEffect, useState } from 'react';
import { Button, Modal, Skeleton } from 'antd';
import { CancelSubjectIcon, CloseXIcon } from 'icons';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { isEmptyArr, useSaveSubject, useSubjectsData } from 'modules/global';

import { Visible } from 'components';

import { useUserProfileInfo } from '../hooks';
import { ISubjectData } from '../models';

interface IProps {
  open: boolean;
  onClose: () => void;
}

const SubjectSelectModal = ({ open, onClose }: IProps) => {
  const [selectedSubjects, setSelectedSubjects] = useState<ISubjectData[]>([]);
  const { subjects, isLoading: subjectsLoading } = useSubjectsData({ isNotAll: true });
  const { userProfileInfo, isLoading: userProfileLoading } = useUserProfileInfo();

  const mutation = useSaveSubject(onClose);
  const t = useTranslations('profile');

  useEffect(() => {
    if (open && userProfileInfo?.subjects) {
      setSelectedSubjects(userProfileInfo.subjects);
    }
  }, [open, userProfileInfo?.subjects]);

  const toggleSubject = (subject: ISubjectData) => {
    setSelectedSubjects(prev =>
      prev.some(s => s.id === subject.id) ? prev.filter(s => s.id !== subject.id) : [...prev, subject]
    );
  };

  const removeSubject = (subjectId: number) => {
    setSelectedSubjects(prev => prev.filter(s => s.id !== subjectId));
  };

  const handleSaveSubjects = () => {
    const formData = new FormData();

    const data = JSON.stringify({
      subject_ids: selectedSubjects.map(subject => subject.id)
    });

    formData.append('data', data);

    mutation.mutate(formData);
  };

  return (
    <div>
      <Modal
        loading={userProfileLoading}
        footer={false}
        open={open}
        centered
        onCancel={() => {
          onClose();
          setSelectedSubjects([]);
        }}
        width="421px"
        styles={{
          content: { padding: '16px', borderRadius: '12px' }
        }}
        closeIcon={
          <div className="flex items-center justify-center rounded-full bg-gray-800 p-1">
            <CloseXIcon size="12" />
          </div>
        }
        title={<span className="text-lg">{t('left_side.fan_tanlash')}</span>}
      >
        <div className="space-y-3">
          <div className="max-h-64 min-h-64 overflow-auto rounded-xl border border-gray-200 p-2">
            <div className="sticky flex items-center justify-between">
              <span className="font-medium text-gray-700">{t('left_side.tanlangan_fanlar')}</span>
              <span className="text-xs text-gray-500">
                {selectedSubjects.length}/{(subjects?.subject || []).length}
              </span>
            </div>
            <div>
              {selectedSubjects.length > 0 ? (
                <div className="mt-2 grid grid-cols-5 gap-3">
                  {selectedSubjects.map(subject => (
                    <div key={subject.id}>
                      <div className="relative flex justify-center rounded-xl border border-2 border-gray-200 py-2">
                        <div
                          onClick={() => removeSubject(subject.id)}
                          className="absolute -right-2 -top-2  cursor-pointer"
                        >
                          <CancelSubjectIcon />
                        </div>
                        <Image
                          src={subject.image}
                          alt={subject.name.slice(0, 1)}
                          loading="lazy"
                          width={32}
                          height={32}
                          title="Subject selected list"
                        />
                      </div>
                      <p className="text-center text-xs">{subject.name.slice(0, 10)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-50  flex items-center justify-center text-gray-400">
                  <span className="w-40 text-center">{t('left_side.fan_tanlanmagan')}</span>
                </div>
              )}
            </div>
          </div>
          <div className="max-h-64 min-h-64 overflow-auto rounded-xl border border-gray-200 p-2">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-medium text-gray-700">{t('left_side.arxiv_fanlar')}</span>
              <span className="text-xs text-gray-500">{(subjects?.subject || []).length}</span>
            </div>
            <div className="grid grid-cols-5 gap-3">
              <Visible
                if={subjectsLoading}
                else={
                  <Visible
                    if={isEmptyArr(subjects?.subject)}
                    else={subjects?.subject?.map(subject => (
                      <div key={subject.id}>
                        <div
                          onClick={() => toggleSubject(subject)}
                          className="flex cursor-pointer justify-center rounded-xl border py-2"
                          style={
                            selectedSubjects.some(s => s.id === subject.id)
                              ? {
                                  backgroundColor: `${subject.color}1F`,
                                  border: `2px solid ${subject.color}`
                                }
                              : {
                                  border: '2px solid #E7E7E7'
                                }
                          }
                        >
                          <Image
                            src={subject.image}
                            alt={subject.name.slice(0, 1)}
                            loading="lazy"
                            width={32}
                            height={32}
                            title="Subject list"
                          />
                        </div>
                        <p className="text-center text-xs">{subject.name.slice(0, 10)}</p>
                      </div>
                    ))}
                  >
                    <div className="h-50  flex items-center justify-center text-gray-400">
                      <span className="w-40 text-center">{t('left_side.fan_tanlanmagan')}</span>
                    </div>
                  </Visible>
                }
              >
                {Array.from({ length: 7 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Skeleton.Button key={index} className="!h-14 !w-16 !rounded-lg" active />
                ))}
              </Visible>
            </div>
          </div>
        </div>

        <Button
          loading={mutation.isLoading}
          onClick={handleSaveSubjects}
          className={`!mt-5 !w-full !border-none ${
            selectedSubjects.length > 0 ? '!bg-green-500 !text-white' : '!bg-gray-300 !text-white'
          }  !rounded-lg !py-5`}
          disabled={selectedSubjects.length <= 0}
        >
          {t('saqlash')}
        </Button>
      </Modal>
    </div>
  );
};

export default SubjectSelectModal;
